import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';

// Muted dot colors matching useObjectView
const COLUMN_DOTS = [
  'bg-stone-400',
  'bg-sky-400',
  'bg-lime-500',
  'bg-rose-400',
  'bg-slate-400',
  'bg-amber-400',
  'bg-teal-400',
  'bg-zinc-500',
];

function RecordCard({ entry, displayFields, fieldDefMap, onClick, isDragOverlay }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Find a title: first text-type field in displayFields
  const titleField = displayFields.find((col) => {
    const fd = col.field_definition_id ? fieldDefMap[col.field_definition_id] : null;
    return fd?.field_type === 'text';
  });
  const title = titleField ? entry[titleField.key || titleField.field_definition_id] : entry.id?.slice(0, 8);

  // Show up to 3 additional fields (non-title)
  const extraFields = displayFields
    .filter((col) => col !== titleField)
    .slice(0, 3);

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={isDragOverlay ? undefined : style}
      className={`bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-gray-300 transition-colors ${
        isDragging ? 'opacity-30' : ''
      } ${isDragOverlay ? 'shadow-lg ring-2 ring-brand-200' : ''}`}
      onClick={() => !isDragging && onClick?.(entry)}
    >
      <div className="flex items-start gap-2">
        <button
          className="mt-0.5 text-gray-300 hover:text-gray-500 cursor-grab flex-shrink-0"
          {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{title || 'Untitled'}</p>
          {extraFields.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {extraFields.map((col) => {
                const key = col.key || col.field_definition_id || col.system_field;
                const val = entry[key];
                if (val === null || val === undefined || val === '') return null;
                const displayVal = typeof val === 'object' ? (val.display_name || JSON.stringify(val)) : String(val);
                return (
                  <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500 truncate">
                    <span className="text-gray-400 flex-shrink-0">{col.label}:</span>
                    <span className="truncate">{displayVal}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({ column, entries, displayFields, fieldDefMap, onRecordClick, colorIndex }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${column.value}`,
    data: { type: 'column', value: column.value },
  });

  const dot = COLUMN_DOTS[colorIndex % COLUMN_DOTS.length];

  return (
    <div className="flex flex-col min-w-[280px] max-w-[320px] flex-shrink-0">
      {/* Column Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className={`flex-shrink-0 w-[7px] h-[7px] rounded-full ${dot}`} />
        <h3 className="text-sm font-medium text-gray-900 truncate">{column.label}</h3>
        <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{entries.length}</span>
      </div>

      {/* Cards Container */}
      <div
        ref={setNodeRef}
        className={`flex-1 space-y-2 p-2 rounded-lg min-h-[100px] transition-colors ${
          isOver
            ? 'bg-brand-50 ring-2 ring-brand-300'
            : 'bg-gray-50'
        }`}
      >
        <SortableContext items={entries.map((e) => e.id)} strategy={verticalListSortingStrategy}>
          {entries.map((entry) => (
            <RecordCard
              key={entry.id}
              entry={entry}
              displayFields={displayFields}
              fieldDefMap={fieldDefMap}
              onClick={onRecordClick}
            />
          ))}
        </SortableContext>

        {entries.length === 0 && (
          <div className="flex items-center justify-center h-20 text-xs text-gray-400">
            No records
          </div>
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({
  columns,
  groupedEntries,
  onMoveRecord,
  onRecordClick,
  displayFields,
  fieldDefMap,
}) {
  const [activeEntry, setActiveEntry] = useState(null);


  // Flatten all entries for drag lookup
  const flatEntries = useMemo(() => {
    const all = [];
    for (const col of columns) {
      const entries = groupedEntries[col.value] || [];
      for (const e of entries) {
        all.push({ ...e, _columnValue: col.value });
      }
    }
    return all;
  }, [columns, groupedEntries]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragStart = useCallback((event) => {
    const entry = flatEntries.find((e) => e.id === event.active.id);
    setActiveEntry(entry || null);
  }, [flatEntries]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveEntry(null);

    if (!over) return;

    const entryId = active.id;
    let targetValue = null;

    // Dropped on column
    if (over.id.toString().startsWith('col-')) {
      targetValue = over.id.toString().replace('col-', '');
    } else {
      // Dropped on another card — find its column
      const targetEntry = flatEntries.find((e) => e.id === over.id);
      if (targetEntry) {
        targetValue = targetEntry._columnValue;
      }
    }

    if (targetValue !== null) {
      const entry = flatEntries.find((e) => e.id === entryId);
      if (entry && entry._columnValue !== targetValue) {
        onMoveRecord?.(entryId, targetValue === '__uncategorized__' ? null : targetValue);
      }
    }
  }, [flatEntries, onMoveRecord]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {columns.map((col, i) => (
          <KanbanColumn
            key={col.value}
            column={col}
            entries={groupedEntries[col.value] || []}
            displayFields={displayFields}
            fieldDefMap={fieldDefMap}
            onRecordClick={onRecordClick}
            colorIndex={i}
          />
        ))}
      </div>

      <DragOverlay>
        {activeEntry ? (
          <RecordCard
            entry={activeEntry}
            displayFields={displayFields}
            fieldDefMap={fieldDefMap}
            isDragOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
